/**
 * Palette curata (~12 token) + default colori tipologia appuntamento (PR-I).
 *
 * FONTE UNICA dependency-free: zero import. Compilabile sia nel bundle
 * browser sia nel runtime Cloud Functions (copiata via codegen in
 * functions/src/tipologie/tipologia-palette.generated.ts).
 *
 * Persistenza (Q4): solo l'id del token, mai l'hex.
 * I valori hex dei default tipologici sono pinnati dal test di non-regressione
 * (mappa completa chiave → { bar, bg }).
 */

/** Identificatori dei ~12 token di palette curata. */
export const PALETTE_TOKEN_IDS = [
  'yellow',
  'pink',
  'orange',
  'blue',
  'lime',
  'red',
  'gray',
  'charcoal',
  'success',
  'warning',
  'studio',
  'tertiary',
] as const;

export type PaletteTokenId = (typeof PALETTE_TOKEN_IDS)[number];

/**
 * Chiavi delle 8 tipologie esistenti. Ordine canonico (filtri, form).
 * Allineato a TipoAppuntamento — non aggiungere/rimuovere/rinominare (D1).
 */
export const TIPOLOGIA_KEYS = [
  'prima-visita',
  'controllo',
  'consegna-dieta',
  'online',
  'coppie',
  'ferie',
  'permesso',
  'altro',
] as const;

export type TipologiaKey = (typeof TIPOLOGIA_KEYS)[number];

export interface PaletteSwatch {
  /** HEX 7-char (#RRGGBB) — asta laterale tipologia. */
  readonly bar: string;
  /** HEX 7-char (#RRGGBB) — wash di sfondo evento. */
  readonly bg: string;
  /** Etichetta IT del token (UI Impostazioni). */
  readonly label: string;
}

export interface TipologiaColorResolved {
  readonly bar: string;
  readonly bg: string;
  readonly label: string;
}

/**
 * Palette curata. I primi 8 token riproducono i colori PR8a.
 * I 4 aggiuntivi riusano token del design system (Q3):
 * success, warning, field-studio (studio), tertiary — wash derivati.
 */
export const TIPOLOGIA_PALETTE: Readonly<Record<PaletteTokenId, PaletteSwatch>> =
  {
    yellow: { bar: '#FFD60A', bg: '#FFF9DB', label: 'Giallo' },
    pink: { bar: '#FF6B9D', bg: '#FFE5EF', label: 'Rosa' },
    orange: { bar: '#FF9F0A', bg: '#FFF3E0', label: 'Arancio' },
    blue: { bar: '#0A84FF', bg: '#E3F2FD', label: 'Blu' },
    lime: { bar: '#A3E635', bg: '#F0FFF0', label: 'Lime' },
    red: { bar: '#FF3B30', bg: '#FFEBEE', label: 'Rosso' },
    gray: { bar: '#8E8E93', bg: '#F0F0F0', label: 'Grigio' },
    charcoal: { bar: '#4A4A4A', bg: '#F7F7F8', label: 'Antracite' },
    success: { bar: '#10B981', bg: '#D1FAE5', label: 'Verde successo' },
    warning: { bar: '#F59E0B', bg: '#FEF3C7', label: 'Ambra' },
    studio: { bar: '#6D28D9', bg: '#EDE9FE', label: 'Viola studio' },
    tertiary: { bar: '#006B2D', bg: '#E8F5E9', label: 'Verde tertiary' },
  };

/** Etichette IT delle tipologie (UI agenda / form). */
export const TIPOLOGIA_LABELS: Readonly<Record<TipologiaKey, string>> = {
  'prima-visita': 'Prima visita',
  controllo: 'Controllo',
  'consegna-dieta': 'Consegna dieta',
  online: 'Online',
  coppie: 'Coppie',
  ferie: 'Ferie',
  permesso: 'Permesso',
  altro: 'Altro',
};

/**
 * Default PR8a: mappa tipologia → id token. Persistenza Firestore usa
 * la stessa forma (solo id, mai hex).
 */
export const TIPOLOGIA_COLOR_DEFAULTS: Readonly<
  Record<TipologiaKey, PaletteTokenId>
> = {
  'prima-visita': 'yellow',
  controllo: 'pink',
  'consegna-dieta': 'orange',
  online: 'blue',
  coppie: 'lime',
  ferie: 'red',
  permesso: 'gray',
  altro: 'charcoal',
};

function isPaletteTokenId(value: string): value is PaletteTokenId {
  return (PALETTE_TOKEN_IDS as ReadonlyArray<string>).includes(value);
}

/**
 * Risolve la mappa completa tipologia → { bar, bg, label }.
 * Config assente, parziale o con id sconosciuti: fallback silenzioso
 * ai default (D7). Mai valori vuoti.
 */
export function resolveTipologiaColori(
  config: Partial<Record<TipologiaKey, string>> | null | undefined
): Readonly<Record<TipologiaKey, TipologiaColorResolved>> {
  const out = {} as Record<TipologiaKey, TipologiaColorResolved>;
  for (const key of TIPOLOGIA_KEYS) {
    const raw = config?.[key];
    const tokenId =
      typeof raw === 'string' && isPaletteTokenId(raw)
        ? raw
        : TIPOLOGIA_COLOR_DEFAULTS[key];
    const swatch = TIPOLOGIA_PALETTE[tokenId];
    out[key] = {
      bar: swatch.bar,
      bg: swatch.bg,
      label: TIPOLOGIA_LABELS[key],
    };
  }
  return out;
}
